import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { WorkoutContext } from "../contexts/WorkoutContext";
import { useAuth } from "../contexts/AuthContext";
import { useUserProfile } from "../contexts/UserProfileContext";
import useSessionShapePreview from "./useSessionShapePreview";
import { useProgramme } from "./useProgramme";
import { nextPendingProgrammeSession } from "../utils/programmeContext";
import { coerceLegacyNodeTypes } from "../utils/planNormaliser";
import { MIXED_STYLE, NEXT_STYLE } from "../utils/mixedStyle";
import { EQUIPMENT_LOCATION_MAP } from "../config/equipment";
import {
  onTodayShapeChange,
  readTodayShape,
  todayShapeContext,
  writeTodayShape,
} from "../utils/todayShape";

/**
 * Today's suggestion: the next programme session, else What's next (with
 * history to read) or Surprise me (a new user). Shared by the Session tab and
 * the Coach at rest (#109), which show the same shape: the one on screen is
 * remembered for the day, so a shuffle in one view moves the other.
 */
const useTodaySuggestion = () => {
  const { userProfile, setActiveProgrammeSession, loadSessionIntoCoach } = useContext(WorkoutContext);
  const { isAuthenticated } = useAuth();
  const { sessions, equipmentPresets, locationPresets } = useUserProfile();
  const { programme, programmes, loading: programmeLoading, assignSession } = useProgramme();
  const programmeSlot = useMemo(
    () => (programme ? nextPendingProgrammeSession(programme) : null),
    [programme],
  );

  const isNewUser = !(isAuthenticated && sessions?.length > 0);
  const selector = isNewUser ? MIXED_STYLE : NEXT_STYLE;
  const sessionMinutes = userProfile.sessionDuration || userProfile.timeAvailable || 45;
  // The kit the generate screen will default to (default equipment preset,
  // else the default location's, else the profile's), so the suggestion
  // doesn't offer a barbell session to someone training with bodyweight.
  const kit = useMemo(() => {
    const preset = equipmentPresets?.find((p) => p.is_default);
    if (Array.isArray(preset?.equipment)) return preset.equipment;
    const location = locationPresets?.find((l) => l.is_default) || locationPresets?.[0];
    const locationKit = location
      ? equipmentPresets?.find((p) => p.id === location.equipment_preset_id)
      : null;
    if (Array.isArray(locationKit?.equipment)) return locationKit.equipment;
    if (userProfile.availableEquipment?.length) {
      return userProfile.availableEquipment;
    }
    return EQUIPMENT_LOCATION_MAP[userProfile.trainingLocation || "Home"] || [];
  }, [equipmentPresets, locationPresets, userProfile.availableEquipment, userProfile.trainingLocation]);

  const shapeContext = todayShapeContext({
    selector,
    minutes: sessionMinutes,
    sessionCount: sessions?.length || 0,
  });
  const [held, setHeld] = useState(() => readTodayShape(shapeContext));
  useEffect(() => {
    setHeld(readTodayShape(shapeContext));
    return onTodayShapeChange(() => setHeld(readTodayShape(shapeContext)));
  }, [shapeContext]);

  const suggestionConfig = useMemo(
    () => ({
      workoutTypes: [selector],
      sessionDuration: sessionMinutes,
      availableEquipment: kit,
      readiness: "Normal",
      useHistoryGuidance: true,
      noFaff: false,
      mixedArchetypeId: held,
    }),
    [selector, sessionMinutes, kit, held],
  );
  const { preview, shuffle: shufflePreview } = useSessionShapePreview({
    planConfig: suggestionConfig,
    userProfile,
    isAuthenticated,
    open: true,
    checkProgramme: false,
  });

  // Remember what's on screen so the other view shows the same.
  const shownId = preview?.archetype?.id || null;
  useEffect(() => {
    if (shownId && shownId !== held) writeTodayShape(shapeContext, shownId);
  }, [shownId, held, shapeContext]);

  const shuffle = useCallback(() => {
    writeTodayShape(shapeContext, null);
    setHeld(null);
    shufflePreview();
  }, [shapeContext, shufflePreview]);

  // The generate screen builds exactly the shape shown here.
  const suggestionOverrides = useCallback(
    () => ({
      workoutTypes: [selector],
      sessionDuration: sessionMinutes,
      mixedArchetypeId: shownId,
    }),
    [selector, sessionMinutes, shownId],
  );

  // Run the programme slot's plan in the Coach, tagged to the slot so
  // finishing it completes the slot.
  const startProgrammeSlot = useCallback(
    (plan) => {
      if (!programme || !programmeSlot || !plan) return;
      setActiveProgrammeSession({
        programmeId: programme.id,
        programmeSessionId: programmeSlot.id,
        label: plan.label || programmeSlot.label,
        scheduledLabel: programmeSlot.scheduled_label,
      });
      loadSessionIntoCoach(coerceLegacyNodeTypes(JSON.parse(JSON.stringify(plan))));
    },
    [programme, programmeSlot, setActiveProgrammeSession, loadSessionIntoCoach],
  );

  return {
    programme,
    programmes,
    programmeLoading,
    assignSession,
    programmeSlot,
    isNewUser,
    selector,
    sessionMinutes,
    preview,
    shuffle,
    suggestionOverrides,
    startProgrammeSlot,
  };
};

export default useTodaySuggestion;
